import React, { useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import withLoomIntegration from './withLoomIntegration'
import enhanceEditor from './enhanceEditor'
import styled from 'styled-components'
import RecordButton from '../../Loom/RecordButton'
import useLoomData from '../../../../hooks/useLoomData'
import {
  contentFromPlaintextWithVideo,
  contentToPlaintextWithVideo
} from '../videoSeralization'
import { LoomContext } from '../../../../containers/LoomProvider'
import withUnsavedWorkWarning from './withUnsavedWorkWarning'

/**
 * HOC to build a fully featured and contained Draft editor with Loom integration
 * Applys withLoomIntegration and enhanceEditor HOCs to base editor that is passed in
 * Wraps core editor in a component with a built in record button and configurable layout
 * Handles all Loom data management
 * Can be used without parent having to deal with any Draft or Loom stuff
 *   - This includes org flag handling - editor will automatically remove the recording
 *     button if the org doesn't allow Loom
 *   - Does still have to pass in a sessionId and recoveryPath/Action
 *
 * Layout options
 * layout: {
 *   minHeight, maxHeight: constain height of entire editor
 *   padding: padding used around edges of editor and between the core editor and record button
 *   inline: if true will make an inline editor with record button at end of editor instead of bottom
 *   recordButtonPlaceholder: if true, will have a placeholder of the record
 *     button when Loom is disabled to keep a consistent layout
 * }
 * Wrapper styles can be provided with className, id, styles, and StyleComponent props
 */

const Wrapper = styled.div.attrs(props => ({
  as: props.StyleComponent
}))`
  ${props => props.minHeight && `min-height: ${props.minHeight}px;`}
  ${props => props.maxHeight && `max-height: ${props.maxHeight}px;`}

  display: ${props => (props.inline ? `inline-flex` : `flex`)};
  flex-direction: ${props => (props.inline ? `row` : `column`)};

  ${props => props.inline && `overflow: auto;`}
`

const EditorWrapperLoose = styled.div`
  flex: 1;
  ${props => !props.inline && `overflow: auto;`}
`

const EditorWrapperTight = styled.div`
  padding: ${props => props.padding}px;
  /* draft adds 5px padding to left  */
  padding-left: ${props => props.padding - 5}px;
  ${props =>
          props.noBottomPadding &&
          (props.inline ? `padding-right: 0;` : `padding-bottom: 0;`)}
  word-break: break-word;
`

const RecordButtonWrapper = styled.div`
  margin: ${props => props.padding}px;
  ${props =>
          props.inline &&
          `
    position: sticky;
    right: 0;
    top: ${props.padding}px;
  `}
`

const makeFullEditorWithLoom = (BaseEditor, isRichEditor) => {
  const CoreEditor = enhanceEditor(
    withUnsavedWorkWarning(
      withLoomIntegration(
        BaseEditor
      )
    )
  )

  const Component = props => {
    const {
      className,
      id,
      style,
      StyleComponent = 'div',
      layout,
      sessionId,
      recoveryPath,
      recoveryAction
    } = props

    const { enabled: enableLoom } = useContext(LoomContext)
    const loomData = useLoomData(sessionId)
    const editor = useRef(null)

    const focusText = () => {
      setTimeout(() => editor.current.focus(), 10)
    }

    return (
      <Wrapper
        id={id}
        className={className}
        style={style}
        StyleComponent={StyleComponent}
        minHeight={layout.minHeight}
        maxHeight={layout.maxHeight}
        inline={layout.inline}
        onMouseDown={focusText}
      >
        <EditorWrapperLoose inline={layout.inline}>
          <EditorWrapperTight
            padding={layout.padding}
            noBottomPadding={enableLoom || layout.recordButtonPlaceholder}
            inline={layout.inline}
          >
            <CoreEditor
              {...props}
              ref={editor}
              initialValue={
                loomData && loomData.recovery && loomData.recovery.content
              }
              loomData={loomData}
              serialize={contentToPlaintextWithVideo}
              deserialize={contentFromPlaintextWithVideo}
              spellCheck="true"
              stripPastedStyles={!isRichEditor}
            />
          </EditorWrapperTight>
        </EditorWrapperLoose>
        {(enableLoom || layout.recordButtonPlaceholder) && (
          <RecordButtonWrapper padding={layout.padding} inline={layout.inline}>
            {enableLoom ? (
              <RecordButton
                sessionId={sessionId}
                recoveryPath={recoveryPath}
                recoveryAction={recoveryAction}
              />
            ) : (
              <div style={{ height: '14px' }}>&nbsp;</div>
            )}
          </RecordButtonWrapper>
        )}
      </Wrapper>
    )
  }

  Component.propTypes = {
    className: PropTypes.string,
    id: PropTypes.string,
    style: PropTypes.object,
    StyleComponent: PropTypes.elementType,
    layout: PropTypes.object,
    sessionId: PropTypes.string.isRequired,
    recoveryPath: PropTypes.string,
    recoveryAction: PropTypes.func
  }

  return Component
}

export default makeFullEditorWithLoom
